/**
 * NavRail — persistent left navigation for hub-like modes.
 *
 * brief-13-shell-nav-rail (SHELL-01…05, CARD-01 badge move already shipped
 * in catalogue-card-v2; not part of this component).
 *
 * Rendered only in hub-like modes (vertical-ref, tier-1-hub) — storefront
 * modes (tier-0, reference) keep TopNav unchanged.
 *
 *  - SHELL-01: primary nav lives here instead of the top bar.
 *  - SHELL-02: capped at <=7 destinations, never lists individual apps.
 *  - SHELL-03: OrgSwitcher sits at the top of the rail, above the nav it scopes.
 *  - SHELL-04: do.place navy chrome (--bg-inverted / --fg-on-navy — the
 *    same dark navy TopNav and every other dark surface already use, not
 *    a separate shade), not Elest.io's near-black + orange.
 *  - SHELL-05: collapses to icon-only <1280px, to an off-canvas drawer <900px.
 *
 * Domain-agnostic: no hostname strings, only route paths and i18n labels.
 */

import { useEffect, useState } from 'react';
import { useAuth } from '../auth/KeycloakProvider';
import { t } from '../i18n';
import type { Route } from '../shell/Router';
import Wordmark from './Wordmark';
import OrgSwitcher from './OrgSwitcher';
import { menuItemsForMode, isMenuItemActive } from './AdminMenu';
import { useConfig } from '../config/ConfigContext';
import { routesForMode } from '../shell/routes';
import ThemeToggle from './ThemeToggle';
import { usePrefs } from '../prefs/PrefsContext';
import type { InstanceMode } from '../config/schema';

interface NavRailProps {
  activePath: Route;
  onNavigate: (path: Route) => void;
  cartCount?: number;
  isB2C?: boolean;
  open: boolean;
  onClose: () => void;
}

interface RailItem {
  label: string;
  path: Route;
  mark: string;
  isActive: (path: Route) => boolean;
  /** Present only for items with an expandable sub-list (Administration). */
  expandable?: boolean;
}

// SHELL-02: a capped list of nouns. Never grows per-application. Sub-items
// (Administration's Tenants/Sub-clients/etc.) are nested under one entry,
// not counted against the cap — see ADMIN_MENU_ITEMS from AdminMenu.tsx.
// decision-2026-07-25-broker-mode-split: destinations are filtered by the mode's
// route table, so the rail can never point at a route this deployment does not
// register — the tenant broker gets the same rail, minus what it does not have,
// plus its own /sandbox/request entry.
function buildRailItems(mode: InstanceMode): RailItem[] {
  const registered = routesForMode(mode) as string[];
  return ([
    { label: t('nav.dashboard'), path: '/dashboard', mark: 'D', isActive: (p) => p === '/dashboard' },
    // brief-17-admin-catalogue-tile-actions: renamed from "Marketplace" — from
    // the do.place admin's own view this is where apps/services are managed
    // and configured for rollout, not a consumer marketplace listing (graph:
    // decision-2026-05-30-admin-broker-functions, "service catalogue
    // management"). TopNav's storefront modes keep nav.marketplace unchanged.
    { label: t('nav.catalog'), path: '/', mark: 'M', isActive: (p) => p === '/' },
    {
      label: t('nav.administration'),
      path: '/admin',
      mark: 'A',
      expandable: true,
      isActive: (p) =>
        p === '/admin' ||
        p === '/admin/sub-clients' ||
        p === '/admin/audit-log' ||
        p === '/admin/subscriptions' ||
        p === '/admin/orders' ||
        p === '/admin/sandbox-provisioning',
    },
    { label: t('nav.sandbox'), path: '/sandbox/request', mark: 'S', isActive: (p) => p === '/sandbox/request' },
  ] as RailItem[]).filter((i) => registered.includes(i.path));
}

function RailAvatar() {
  const { user, logout } = useAuth();
  const { prefs, setTheme } = usePrefs();
  const initials = user
    ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase() ||
      user.email.charAt(0).toUpperCase()
    : '?';

  return (
    <div className="dp-rail__footer">
      <div className="dp-rail__account">
        <span className="dp-rail__avatar" aria-hidden="true">{initials}</span>
        <span className="dp-rail__label dp-rail__account-text">
          <span className="dp-rail__account-name">{user?.firstName} {user?.lastName}</span>
          <span className="dp-rail__account-email">{user?.email}</span>
        </span>
      </div>
      <div className="dp-rail__theme">
        <ThemeToggle choice={prefs.theme} onChange={setTheme} />
      </div>

      <button type="button" className="dp-rail__logout" onClick={logout}>
        <span aria-hidden="true">⎋</span>
        <span className="dp-rail__label">{t('nav.logout')}</span>
      </button>
    </div>
  );
}

export default function NavRail({ activePath, onNavigate, cartCount = 0, isB2C = false, open, onClose }: NavRailProps) {
  const { instance } = useConfig();
  const items = buildRailItems(instance.mode);
  const adminSubItems = menuItemsForMode(instance.mode);
  const isUnderAdmin = items.find((i) => i.expandable)?.isActive(activePath) ?? false;

  // Expandable items (Administration) open automatically when the active
  // route is one of their sub-items, and stay togglable by hand otherwise.
  const [adminExpanded, setAdminExpanded] = useState(isUnderAdmin);
  useEffect(() => {
    if (isUnderAdmin) setAdminExpanded(true);
  }, [isUnderAdmin]);

  // Escape closes the mobile drawer.
  useEffect(() => {
    function keyHandler(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  }, [onClose]);

  return (
    <>
      {open && <div className="dp-rail__scrim" onClick={onClose} aria-hidden="true" />}

      <nav
        className={`dp-rail${open ? ' dp-rail--open' : ''}`}
        aria-label={t('nav.railLabel')}
      >
        <div className="dp-rail__head">
          <Wordmark />
        </div>

        {/* SHELL-03: org switcher above the nav it scopes */}
        <div className="dp-rail__switcher">
          <OrgSwitcher />
        </div>

        <ul role="list" className="dp-rail__list">
          {items.map((item) => {
            const isActive = item.isActive(activePath);
            const isExpanded = item.expandable ? adminExpanded : false;

            return (
              <li key={item.path}>
                <div className="dp-rail__row">
                  <button
                    type="button"
                    className={`dp-rail__item${isActive ? ' dp-rail__item--active' : ''}`}
                    onClick={() => {
                      onNavigate(item.path);
                      if (item.expandable) setAdminExpanded(true);
                      onClose();
                    }}
                    aria-current={isActive ? 'page' : undefined}
                    title={item.label}
                  >
                    <span className="dp-rail__mark" aria-hidden="true">{item.mark}</span>
                    <span className="dp-rail__label">{item.label}</span>
                  </button>

                  {item.expandable && (
                    <button
                      type="button"
                      className="dp-rail__chevron"
                      aria-label={isExpanded ? t('nav.closeMenu') : t('nav.openMenu')}
                      aria-expanded={isExpanded}
                      onClick={(e) => {
                        e.stopPropagation();
                        setAdminExpanded((v) => !v);
                      }}
                    >
                      <span aria-hidden="true">{isExpanded ? '▾' : '▸'}</span>
                    </button>
                  )}
                </div>

                {item.expandable && isExpanded && (
                  <ul role="list" className="dp-rail__sublist">
                    {adminSubItems.map((sub, idx) => {
                      const subActive = isMenuItemActive(sub, activePath);
                      return (
                        <li key={`${sub.label}-${idx}`}>
                          <button
                            type="button"
                            className={`dp-rail__subitem${subActive ? ' dp-rail__subitem--active' : ''}`}
                            onClick={() => {
                              onNavigate(sub.path);
                              onClose();
                            }}
                            aria-current={subActive ? 'page' : undefined}
                          >
                            <span className="dp-rail__label">{sub.label}</span>
                            {sub.isPlaceholder && (
                              <span className="dp-rail__soon dp-rail__label">soon</span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}

          {/* Cart — B2C only, structurally absent in B2B (WP-SL-T4) */}
          {isB2C && cartCount > 0 && (
            <li>
              <button
                type="button"
                className={`dp-rail__item${activePath === '/cart' ? ' dp-rail__item--active' : ''}`}
                onClick={() => {
                  onNavigate('/cart');
                  onClose();
                }}
                title="Cart"
              >
                <span className="dp-rail__mark dp-rail__mark--cart" aria-hidden="true">{cartCount}</span>
                <span className="dp-rail__label">Cart</span>
              </button>
            </li>
          )}
        </ul>

        <RailAvatar />
      </nav>
    </>
  );
}
